import Column from '@amzn/meridian/column';
import Link from '@amzn/meridian/link';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Textarea from '@amzn/meridian/textarea';
import Theme from '@amzn/meridian/theme';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHrpsApi } from '../../clients/useHrpsApi';
import {
    ADD_SHIPPING_LOCATION_REQUEST,
    defaultRequestTypeForIssueType,
    GENERAL_ISSUE,
    OTHER_REQUEST,
    REQUEST_TYPES,
    RequestType,
    requestTypeById,
} from '../../constants';
import { THEME } from '../../theme';
import { Logger } from '../../utils/logger';
import { Button, Select, SelectOption, TranslatedString, useTranslation } from '../blocks/localization';
import { LabeledInput, usePageMessaging } from '../composites';
import LabeledField from '../composites/LabeledField';

const MODAL_WIDTH = 600;

export type DraftContext = {
    extractionId: string;
};

export type DraftPrefill = {
    poNumber?: string;
    shipToAddress?: string;
};

export interface CreateIssueModalProps {
    dataTestId?: string;
    label?: TranslatedString;
    labelType?: 'primary' | 'secondary';
    header?: TranslatedString;
    username: string;
    resellerId: string;
    template?: string;
    pageData?: {};
    useButton?: boolean;
    onSubmitSuccessCallback?: any;
    initialOpen?: boolean;
    showTypePicker?: boolean;
    requestType?: string;
    draftContext?: DraftContext;
    draftPrefill?: DraftPrefill;
    onClose?: () => void;
}

function draftContextLines(resellerId: string, { extractionId }: DraftContext): string {
    return `Reseller: ${resellerId}\nExtraction id: ${extractionId}`;
}

function buildIssueBody(description: string, resellerId: string, draftContext?: DraftContext): string {
    return draftContext ? `${description}\n\n${draftContextLines(resellerId, draftContext)}` : description;
}

const TRANSLATION_NAMESPACES = ['createNotificationPopover', 'labeledInput', 'returnOrder'];

const CreateIssueModal = ({
    label,
    labelType = 'primary',
    header,
    username,
    resellerId,
    template,
    dataTestId,
    pageData,
    useButton,
    onSubmitSuccessCallback,
    initialOpen,
    showTypePicker,
    requestType,
    draftContext,
    draftPrefill,
    onClose,
}: CreateIssueModalProps) => {
    const translationNSs = template ? [...TRANSLATION_NAMESPACES, template] : TRANSLATION_NAMESPACES;
    const { t, ready } = useTranslation(translationNSs);
    const [open, setOpen] = useState(false);
    const [subject, setSubject] = useState('');
    const [description, setDescription] = useState('');
    const [issueType, setIssueType] = useState('');
    const [issuePrompt, setIssuePrompt] = useState('');
    const [requestTypeId, setRequestTypeId] = useState('');
    const [componentIsBusy, setComponentIsBusy] = useState(false);

    const {
        issues: { createIssue },
    } = useHrpsApi();
    const { showSuccess, showError } = usePageMessaging();

    const requestContextLines = useCallback(
        (selected: RequestType): string[] => {
            if (selected.id === ADD_SHIPPING_LOCATION_REQUEST) {
                return [
                    t('requestContext-addressLine-text', { address: draftPrefill?.shipToAddress ?? '' }),
                    t('requestContext-addressHint-text'),
                ];
            }
            return selected.contextKey ? [t(selected.contextKey)] : [];
        },
        [t, draftPrefill]
    );

    const applyRequestType = useCallback(
        (id: string) => {
            const selected = requestTypeById(id);
            setRequestTypeId(selected.id);
            setIssueType(selected.issueType);
            setSubject(t(selected.subjectKey));
            const contextLines = requestContextLines(selected);
            const poNumber = draftPrefill?.poNumber;
            const purchaseOrderLine = poNumber ? t('requestContext-purchaseOrderLine-text', { poNumber }) : '';
            const lines = contextLines.length ? [purchaseOrderLine, ...contextLines] : [];
            setDescription(lines.filter((line) => line).join('\n'));
            setIssuePrompt('');
        },
        [t, draftPrefill, requestContextLines]
    );

    const loadTemplateValues = useCallback(() => {
        Logger.debug(`**** Loading Templated values ${template}`);
        const templateIssueType = t(`${template}:issue-type`);
        setSubject(t(`${template}:issue-subject`, { ...pageData }));
        setDescription(t(`${template}:issue-context`, { ...pageData, defaultValue: '' }));
        setIssuePrompt(t(`${template}:issue-description`, { ...pageData }));
        setIssueType(templateIssueType);
        setRequestTypeId(requestType ?? defaultRequestTypeForIssueType(templateIssueType).id);
    }, [t, template, pageData, requestType]);

    const loadInitialValues = useCallback(() => {
        if (template) {
            loadTemplateValues();
        } else if (requestType) {
            applyRequestType(requestType);
        } else {
            Logger.debug(`**** No Template Using Default Values`);
            setSubject('');
            setDescription('');
            setIssueType(GENERAL_ISSUE);
            setRequestTypeId(OTHER_REQUEST);
        }
    }, [template, requestType, loadTemplateValues, applyRequestType]);

    const hasInitialized = useRef(false);
    useEffect(() => {
        if (initialOpen && ready && !hasInitialized.current) {
            hasInitialized.current = true;
            loadInitialValues();
            setOpen(true);
        }
    }, [initialOpen, ready, loadInitialValues]);

    const onClickButton = () => {
        loadInitialValues();
        setOpen(true);
    };

    const closeModal = () => {
        setSubject('');
        setDescription('');
        setOpen(false);
        if (onClose) onClose();
    };

    async function submitNotification() {
        if (subject && description) {
            setComponentIsBusy(true);
            try {
                await createIssue({
                    resellerId: resellerId,
                    issueTitle: subject,
                    issueBody: buildIssueBody(description, resellerId, draftContext),
                    issueType: issueType,
                });
                showSuccess(t('createNotification-successMessage'));
                if (onSubmitSuccessCallback) onSubmitSuccessCallback();
                closeModal();
            } catch {
                showError(t('createNotification-errorMessage'));
            } finally {
                setComponentIsBusy(false);
            }
        }
    }

    return (
        <>
            {label &&
                (useButton ? (
                    <Button onClick={onClickButton} type={labelType} data-testid={`${dataTestId}.button`}>
                        {label}
                    </Button>
                ) : (
                    <Link onClick={onClickButton} type={labelType} data-testid={`${dataTestId}.link`}>
                        {label}
                    </Link>
                ))}
            <Theme tokens={THEME.lightTokens}>
                <Modal
                    open={open}
                    onClose={closeModal}
                    title={header}
                    width={MODAL_WIDTH}
                    closeLabel={t('closeModal-label')}
                    data-testid={`${dataTestId}.modal`}
                >
                    <Column>
                        {showTypePicker && (
                            <Column spacing={'xsmall'}>
                                <Select
                                    label={t('requestType-label')}
                                    value={requestTypeId}
                                    onChange={applyRequestType}
                                    data-testid={'createIssueModal.requestTypeSelect'}
                                >
                                    {REQUEST_TYPES.map((option) => (
                                        <SelectOption key={option.id} value={option.id} label={t(option.labelKey)} />
                                    ))}
                                </Select>
                                <Text type={'b200'} data-testid={'createIssueModal.handledByText'}>
                                    {t('handledBy-label', { team: t(requestTypeById(requestTypeId).routingKey) })}
                                </Text>
                            </Column>
                        )}
                        <LabeledField label={t('reseller-label')} text={t.cognate(resellerId)} />
                        <LabeledField label={t('createdByUser-label')} text={t.cognate(username)} />
                        <LabeledInput
                            label={t.cognate(`${t('notificationSubject-label')} *`)}
                            type={'text'}
                            onChange={setSubject}
                            value={subject}
                            dataTestId={'createIssueModal.subjectField'}
                        />
                        {issuePrompt && (
                            <div style={{ whiteSpace: 'pre-line' }} data-testid={'createIssueModal.descriptionText'}>
                                <Text>{issuePrompt}</Text>
                            </div>
                        )}
                        <Textarea
                            value={description}
                            label={t.cognate(`${t('notificationText-label')} *`)}
                            onChange={setDescription}
                            data-testid={'createIssueModal.bodyTextarea'}
                        />
                        <Text type={'b200'}>
                            {'* '}
                            {t('requiredField-label')}
                        </Text>
                    </Column>
                    <ModalFooter>
                        <Row alignmentHorizontal={'right'} widths={'fit'}>
                            <Button
                                type={'secondary'}
                                onClick={closeModal}
                                data-testid={'createIssueModal.cancelButton'}
                            >
                                {t('cancelNotification-button')}
                            </Button>
                            <Button
                                type={'primary'}
                                onClick={submitNotification}
                                data-testid={'createIssueModal.createButton'}
                                disabled={componentIsBusy || !description.trim() || !subject.trim()}
                            >
                                {t('createNotification-button')}
                            </Button>
                        </Row>
                    </ModalFooter>
                </Modal>
            </Theme>
        </>
    );
};

export { CreateIssueModal };
